import { Controller } from "@hotwired/stimulus"

export default class AddressController extends Controller {
  static targets = [
    "address",
    "city",
    "state",
    "country",
    "postalCode"
  ]

  connect() {
    if (!globalThis.google || !google.maps?.places) {
      console.error("Google Places API not loaded")
      return
    }

    this._placeId = null
    this._selectedAddress = null

    this.setupAutocomplete()
    this.setupManualEditDetection()
    this.setupPacListeners()
  }

  setupPacListeners() {
    this._pacLocked = false;
    this._hidePac = this.hidePac.bind(this);
    this._beforeCache = this.beforeCache.bind(this);

    // close on scroll (desktop + trackpad + mobile)
    globalThis.addEventListener('scroll', this._hidePac, { passive: true });
    globalThis.addEventListener('wheel', this._hidePac, { passive: true });
    globalThis.addEventListener('touchmove', this._hidePac, { passive: true });

    document.addEventListener('turbo:before-cache', this._beforeCache);

    // lock when user interacts with the suggestions
    this._onPointerDown = (e) => {
      if (e.target.closest('.pac-container')) this._pacLocked = true;
    };

    this._onPointerUp = (e) => {
      if (!e.target.closest('.pac-container')) this._pacLocked = false;
    };

    document.addEventListener('pointerdown', this._onPointerDown, true);
    document.addEventListener('pointerup', this._onPointerUp, true);
    document.addEventListener('pointercancel', this._onPointerUp, true);
  }

  disconnect() {
    globalThis.removeEventListener('scroll', this._hidePac);
    globalThis.removeEventListener('wheel', this._hidePac);
    globalThis.removeEventListener('touchmove', this._hidePac);

    document.removeEventListener('turbo:before-cache', this._beforeCache);

    document.removeEventListener('pointerdown', this._onPointerDown, true);
    document.removeEventListener('pointerup', this._onPointerUp, true);
    document.removeEventListener('pointercancel', this._onPointerUp, true);
  }

  hidePac(e) {
    // don't hide while interacting with dropdown
    if (this._pacLocked) return;

    // don't hide if the scroll/wheel/touchmove is inside the dropdown list
    if (e?.target?.closest?.('.pac-container')) return;

    // optional: if mouse is hovering the dropdown (desktop)
    const pac = document.querySelector('.pac-container');
    if (pac && pac.matches(':hover')) return;

    document.querySelectorAll('.pac-container').forEach((el) => {
      el.style.display = 'none';
    });
  }

  beforeCache() {
    document.querySelectorAll('.pac-container').forEach((el) => el.remove());
  }

  setupAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(this.addressTarget, {
      types: ["geocode"],
      fields: ["place_id", "address_components", "formatted_address", "geometry"]
    })

    this.autocomplete.addListener("place_changed", async () => {
      const place = this.autocomplete.getPlace()
      if (!place?.address_components) return

      this._placeId = place.place_id
      this._selectedAddress = place.formatted_address

      const components = this.extractComponents(place.address_components)

      const addressLine1 = [components.streetNumber, components.route]
        .filter(Boolean)
        .join(" ")

      if (this.element.dataset.controller?.includes('fill-address')) {
        this.addressTarget.value = place?.formatted_address?.split(',')[0] || ""
        const fillAddressController =
          this.application.getControllerForElementAndIdentifier(
            this.element,
            "fill-address"
          );

        if (fillAddressController) {
          await fillAddressController.setAddressComponents(components);
        }
      } else {
        this.addressTarget.value = addressLine1 || place.formatted_address || ""
        if (this.hasCityTarget) this.cityTarget.value = components.city || ""
        if (this.hasStateTarget) this.stateTarget.value = components.state || ""
        if (this.hasCountryTarget) this.countryTarget.value = components.country || ""
        if (this.hasPostalCodeTarget) this.postalCodeTarget.value = components.postalCode || ""
      }

      if (!components.postalCode && place.geometry && this.hasPostalCodeTarget) {
        this.lookupPostalCode(place.geometry.location)
      }

      $(this.addressTarget).data("address-selected", true)
      $(this.addressTarget)?.closest('form')?.valid()
      $(this.addressTarget).valid()
    })
  }

  setupManualEditDetection() {
    this.addressTarget.addEventListener("input", () => {
      if (this.addressTarget.value !== this._selectedAddress) {
        this._placeId = null
      }
    })
  }

  extractComponents(components) {
    const result = {
      streetNumber: "",
      route: "",
      city: "",
      state: "",
      country: "",
      postalCode: ""
    }

    for (const component of components) {
      const t = component.types

      if (t.includes("street_number")) result.streetNumber = component.long_name
      if (t.includes("route")) result.route = component.long_name
      if (t.includes("locality") || t.includes("administrative_area_level_3")) {
        result.city ||= component.long_name
      }
      if (t.includes("administrative_area_level_1")) result.state = component.long_name
      if (t.includes("country")) result.country = component.short_name
      if (t.includes("postal_code")) result.postalCode = component.long_name
    }

    return result
  }

  lookupPostalCode(location) {
    const geocoder = new google.maps.Geocoder()

    geocoder.geocode({ location }, (results, status) => {
      if (status !== "OK" || !results?.length) return

      const fallback = this.extractComponents(results[0].address_components)

      if (fallback.postalCode) {
        this.postalCodeTarget.value = fallback.postalCode
        $(this.postalCodeTarget).valid?.()
      }
    })
  }

  validateAddress() {
    if (!this._placeId && this?.addressTarget?.value?.length === 0) {
      this.addressTarget.value = ""
    }

    $(this.addressTarget).valid?.()
  }
}
